import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogContent,
  Grid,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
import UploadIcon from '@mui/icons-material/Upload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { product } from '../../../constant/product'
import {
  useGetEndUserAppleLuckyDrawCanSubmit,
  usePostEndUserAppleLuckyDraw,
} from '../../../api/promotions/end-user/end-user-apple-lucky-draw'
import { useNavigate } from 'react-router-dom'

const MAX_FILE_SIZE = 5 // MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg']

const StyledFieldBox = styled(Box)`
  && {
    max-width: 800px;
    justify-content: space-evenly;
    min-height: 80px;
    margin: auto;
    display: flex;
    flex-direction: column;
  }
`

const AppleLuckyDrawForm = () => {
  const { register, handleSubmit, control, setValue, setError } = useForm({
    mode: 'onChange',
  })

  const navigate = useNavigate()
  const { mutateAsync, isLoading } = usePostEndUserAppleLuckyDraw()

  const { data } = useGetEndUserAppleLuckyDrawCanSubmit()

  const { enqueueSnackbar } = useSnackbar()

  const [openDialog, setopenDialog] = useState(false)

  const onSubmit = async (data) => {
    let formData = new FormData()

    for (let key in data) {
      // Add files
      if (['batteryPhoto'].includes(key)) {
        formData.append(key, data[key][0])
      } else {
        formData.append(key, data[key])
      }
    }

    try {
      const res = await mutateAsync(formData)

      setopenDialog(true)

      return res
    } catch (error) {
      // Display error on popup
      enqueueSnackbar(error?.response?.data?.message ?? 'Submit fail', {
        variant: 'error',
        autoHideDuration: 7000,
      })
    }
  }

  const handleClose = () => {
    // Close dialog
    setopenDialog(false)

    // Navigate back to home page
    navigate('/')

    // Scroll back to top
    window.scrollTo(0, 0)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledFieldBox>
          <Controller
            name='name'
            control={control}
            defaultValue=''
            rules={{
              required: 'Name is required',
              maxLength: { value: 200, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Name'
                fullWidth
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='phone'
            control={control}
            defaultValue=''
            rules={{
              required: 'Phone number is required',
              pattern: {
                value: /^((601)[0-46-9]-*[0-9]{7,8}|(65)[689]-*[0-9]{7})$/,
                message: `Invalid phone number. Use 601xxxxxxxx (Malaysia) or 65xxxxxxxx (Singapore). '+' sign is not required`,
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Phone number'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                placeholder='60127123456'
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='workshop'
            control={control}
            defaultValue=''
            rules={{
              required: 'Workshop is required',
              maxLength: { value: 200, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Workshop Name'
                fullWidth
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='brand'
            control={control}
            rules={{
              required: 'Brand is required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                options={product}
                onChange={(e, data) => onChange(data)}
                value={value || ''}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='Brand'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='serialNumber'
            control={control}
            defaultValue=''
            rules={{
              required: 'Battery Serial number is required',
              pattern: {
                value: /^((V[XYZ]|E[YZ])\d{7}|[VE][01]\d{6})$/i, // case insensitive
                message:
                  'Invalid battery serial number format - VXxxxxxxx / VYxxxxxxx / EYxxxxxxx / VZxxxxxxx / EZxxxxxxx / V0xxxxxx / E0xxxxxx / V1xxxxxx / E1xxxxxx',
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const handleChange = (event) => {
                const sanitizedValue = event.target.value.replace(/\s/g, '').toUpperCase()
                onChange(sanitizedValue)
              }
              return (
                <TextField
                  label='Battery Serial Number'
                  value={value}
                  onChange={handleChange}
                  error={!!error}
                  fullWidth
                  helperText={error ? error.message : null}
                />
              )
            }}
          />
        </StyledFieldBox>
        <StyledFieldBox display='flex' flexDirection='column'>
          {/* Sample for users to used as reference */}
          <Typography variant='h2' mt={2} mb={2}>
            Sample of the battery with serial number
          </Typography>
          <Grid container justifyContent={'space-between'} gap={4}>
            <Grid item>
              <Typography variant='h3' mt={2} mb={2}>
                Varta with serial number
              </Typography>
              <img
                src='/assets/promotions/end-user/apple-products-lucky-draw/sample_varta.png'
                alt='sample_varta_with_serial_number'
                style={{ maxHeight: '380px' }}
              />
            </Grid>
            {/* Temp removal due to incorrect sample pic */}
            {/**
                          <Grid item>
              <Typography variant='h3' mt={2} mb={2}>
                Energizer with serial number
              </Typography>
              <img
                src='/assets/promotions/end-user/apple-products-lucky-draw/sample_energizer.png'
                alt='sample_energizer_with_serial_number'
                style={{ maxHeight: '380px' }}
              />
            </Grid>
              
               */}
          </Grid>

          <Box mt={4}>
            <Controller
              name='batteryPhoto'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('batteryPhoto')
                // React Hook Form does not block field value being set on failing validation
                // This is a workaround by manually handling both the file size validation and value assignment
                const handleOnChange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2

                  const fileType = e?.target?.files[0]?.type

                  let errorMsg = null

                  // Validate file size
                  if (fileMb > MAX_FILE_SIZE) {
                    // Set error message
                    errorMsg = 'File size must be 5 MB or smaller'
                  } else if (!ALLOWED_FILE_TYPES.includes(fileType)) {
                    // Validate file type
                    errorMsg = 'Only PNG and JPG files are allowed'
                  }

                  // If there are errors
                  if (errorMsg) {
                    // Set error
                    setError(props.name, {
                      type: 'manual',
                      message: errorMsg,
                    })

                    // Reset field value
                    setValue(props.name, null)
                  } else {
                    // Set field value
                    props.onChange(e)
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload a photo of battery with serial number
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={handleOnChange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>

        <StyledFieldBox>
          <Box my={2}>
            <Typography variant='h4'>
              * Only one (1) HT battery serial number may be submitted per submission.
            </Typography>
            <Typography variant='h4'>
              * The maximum submission allowed per person is two (2). Any additional submissions
              using the same phone number will be automatically rejected to prevent duplicates.
            </Typography>
            <Typography variant='h4'>
              * Purchase any VARTA® or Energizer® automotive batteries from participating dealers
              from 1st July 2026 until 30th September 2026.
            </Typography>
            <Typography variant='h4'>
              * The battery serial number must match in the HT Eco Battery Sdn Bhd system. If the
              provided serial number is not found in the system, it will be considered ineligible
              for the campaign submission.
            </Typography>
            <Typography variant='h4'>
              * By submitting this form, you confirm that you have read, understood, and will
              respect the{' '}
              <a
                style={{ color: '#002984' }}
                href={
                  '/assets/promotions/end-user/apple-products-lucky-draw/TermsAndConditions.pdf'
                }
                target='_blank'
                rel='noopener noreferrer'
              >
                Terms & Conditions
              </a>{' '}
              &{' '}
              <a
                style={{ color: '#002984' }}
                href={'/assets/promotions/end-user/apple-products-lucky-draw/PrivacyPolicy.pdf'}
                target='_blank'
                rel='noopener noreferrer'
              >
                Privacy Policy.
              </a>
            </Typography>
          </Box>
          <Box>
            <Button
              fullWidth
              type='submit'
              size='large'
              variant='contained'
              disabled={isLoading || !data?.allow}
              endIcon={isLoading && <CircularProgress style={{ color: '#fff' }} size='1rem' />}
            >
              Submit
            </Button>
            <Typography variant='body2' color='error' mt={1}>
              {(() => {
                // If not allowed, return the message
                if (!data?.allow) return data?.message
                else if (isLoading) return 'Please wait while we process your submission.'
                else return null
              })()}
            </Typography>
          </Box>
        </StyledFieldBox>
      </form>
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogContent dividers>
          <Box display='flex' justifyContent='flex-start' alignItems='center' pb={3}>
            <CheckCircleIcon style={{ color: '#4caf50' }} />
            <Typography variant={'h3'}>{'Form Submitted.'}</Typography>
          </Box>
          <Box mb={1}>
            <Typography gutterBottom>Thank you for your submission.</Typography>

            <Typography>
              We have received your information and will process it within two to three business
              days. You will receive approval or rejection SMS for Apple lucky draw once it has been
              approved/rejected.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default AppleLuckyDrawForm
